import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getB2BCategories, getB2BProductsByCategory } from '../api';
import { useCart } from '../CartContext';

export default function B2BHomePage() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [catsError, setCatsError] = useState('');
  const [popularProducts, setPopularProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [added, setAdded] = useState({});

  const slides = [
    { bg: '/banners/B2b Grocery website.png' },
    { bg: '/banners/hyderabad-grocery.png' },
    { bg: '/banners/B2b slider-3.png' },
    { bg: '/banners/B2b slider-4.png' },
  ];

  useEffect(() => {
    const t = setInterval(() => setCurrentSlide(p => (p + 1) % slides.length), 3000);
    return () => clearInterval(t);
  }, [slides.length]);

  // Fetch B2B categories from DB
  useEffect(() => {
    let active = true;
    setCatsError('');
    getB2BCategories()
      .then(async res => {
        if (!active) return;
        if (!res.success) {
          setCatsError(res.message || 'Unable to load wholesale categories.');
          return;
        }
        setCategories(res.categories || []);
        // Fetch 2 products from first 5 categories = 10 popular products
        const cats = (res.categories || []).slice(0, 5);
        const results = await Promise.allSettled(cats.map(c => getB2BProductsByCategory(c.id)));
        if (!active) return;
        const prods = results.flatMap(r =>
          r.status === 'fulfilled' && r.value.success
            ? r.value.products.slice(0, 2)
            : []
        );
        setPopularProducts(prods);
      })
      .catch(() => {
        if (active) setCatsError('Unable to reach the server. Please refresh and try again.');
      })
      .finally(() => { if (active) setLoadingCats(false); });
    return () => { active = false; };
  }, []);

  const handleQty = (id, val, minOrder = 1) => {
    const q = Math.max(minOrder, parseInt(val) || minOrder);
    setQuantities(prev => ({ ...prev, [id]: q }));
  };

  const handleAdd = (product) => {
    const qty = quantities[product.id] || product.min_order || 1;
    addToCart({ 
      id: product.id, 
      name: product.name, 
      price: parseFloat(product.price), 
      image: product.image_url, 
      category: product.category, 
      unit: product.unit || 'kg', 
      min_order: product.min_order || 1, // Pass min_order
      isB2B: true 
    }, qty, 'b2b');
    setAdded(prev => ({ ...prev, [product.id]: true }));
    setTimeout(() => setAdded(prev => ({ ...prev, [product.id]: false })), 1400);
  };

  const handleCategoryClick = (cat) =>
    navigate(`/b2b/products/${encodeURIComponent(cat.name)}/${cat.id}`);

  const features = [
    { icon: '📦', title: 'Bulk Discounts',     desc: 'Save up to 40% on bulk orders' },
    { icon: '🚚', title: 'Free Delivery',       desc: 'Free shipping on orders above ₹50,000' },
    { icon: '📄', title: 'GST Invoice',         desc: 'Proper GST invoices for your business' },
    { icon: '🤝', title: 'Dedicated Support',   desc: '24/7 support for business clients' },
  ];

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .b2b-hero-container {
            margin: 0.5rem 0 !important;
          }
          .b2b-hero-slider {
            min-height: 300px !important;
            border-radius: 12px !important;
          }
          .b2b-hero-slide {
            min-height: 300px !important;
          }
          .b2b-hero-slide img {
            max-height: 350px !important;
          }
          .b2b-hero-dots {
            bottom: 8px !important;
          }
          .b2b-hero-dot {
            width: 6px !important;
            height: 6px !important;
            margin: 0 3px !important;
          }
          .b2b-hero-arrow {
            width: 28px !important;
            height: 28px !important;
            font-size: 0.7rem !important;
            left: 8px !important;
          }
          .b2b-hero-arrow.right {
            left: auto !important;
            right: 8px !important;
          }
          .b2b-features-grid {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)) !important;
            gap: 0.75rem !important;
          }
          .b2b-feature-card {
            padding: 12px !important;
          }
          .b2b-feature-icon {
            font-size: 1.3rem !important;
          }
          .b2b-feature-title {
            font-size: 0.8rem !important;
          }
          .b2b-feature-desc {
            font-size: 0.7rem !important;
          }
          .b2b-section {
            padding: 1.5rem 0 !important;
          }
          .b2b-section-title {
            font-size: 1rem !important;
            margin-bottom: 1rem !important;
          }
          /* Fix + Add button text alignment and font size on mobile */
          .b2b-add-btn-fix {
            text-align: left !important;
            justify-content: flex-start !important;
            font-size: 0.65rem !important;
          }
        }
        @media (max-width: 480px) {
          .b2b-hero-slider {
            min-height: 250px !important;
          }
          .b2b-hero-slide {
            min-height: 250px !important;
          }
          .b2b-hero-slide img {
            max-height: 300px !important;
          }
          .b2b-hero-arrow {
            width: 24px !important;
            height: 24px !important;
            font-size: 0.6rem !important;
          }
          .b2b-features-grid {
            grid-template-columns: 1fr !important;
            gap: 0.5rem !important;
          }
          .b2b-feature-card {
            padding: 10px !important;
            flex-direction: row !important;
            text-align: left !important;
          }
          .b2b-section {
            padding: 1rem 0 !important;
          }
          /* Fix + Add button text alignment and font size on small mobile */
          .b2b-add-btn-fix {
            text-align: left !important;
            justify-content: flex-start !important;
            font-size: 0.6rem !important;
          }
        }
      `}</style>
      {/* ── Hero Slider ── */}
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <div className="container b2b-hero-container">
          <div style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', background: '#000', minHeight: 450 }} className="b2b-hero-slider">
            <div style={{ display: 'flex', transition: '0.5s', transform: `translateX(-${currentSlide * 100}%)` }}>
              {slides.map((slide, i) => (
                <div key={i} style={{ minWidth: '100%', minHeight: '450px', position: 'relative', background: '#000' }} className="b2b-hero-slide">
                  <img src={slide.bg} alt="banner" style={{ position: 'absolute', width: '100%', height: 'auto', maxHeight: 550, objectFit: 'cover', top: 0, left: 0, display: 'block' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
                </div>
              ))}
            </div>
            {/* Dots */}
            <div style={{ position: 'absolute', bottom: '10px', width: '100%', textAlign: 'center' }} className="b2b-hero-dots">
              {slides.map((_, i) => (
                <span key={i} onClick={() => setCurrentSlide(i)} style={{ display: 'inline-block', width: 8, height: 8, margin: '0 4px', borderRadius: '50%', background: currentSlide === i ? 'white' : 'rgba(255,255,255,0.5)', cursor: 'pointer' }} className="b2b-hero-dot" />
              ))}
            </div>
            {/* Arrows */}
            {[['◀', 'left', () => setCurrentSlide(p => p === 0 ? slides.length - 1 : p - 1)],
              ['▶', 'right', () => setCurrentSlide(p => (p + 1) % slides.length)]].map(([label, side, fn]) => (
              <button key={side} onClick={fn} style={{ position: 'absolute', [side]: '12px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.35)', color: 'white', border: 'none', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', fontSize: '0.85rem' }} className={`b2b-hero-arrow ${side === 'right' ? 'right' : ''}`}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Features ── */}
      <div style={{ padding: '2.5rem 0', background: '#fff', borderBottom: '1px solid #e5e7eb' }} className="b2b-section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }} className="b2b-features-grid">
            {features.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#fafafa' }} className="b2b-feature-card">
                <span style={{ fontSize: '1.6rem', flexShrink: 0 }} className="b2b-feature-icon">{f.icon}</span>
                <div>
                  <p style={{ fontWeight: 700, fontSize: '0.875rem', color: '#111827', margin: 0 }} className="b2b-feature-title">{f.title}</p>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '2px 0 0' }} className="b2b-feature-desc">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Wholesale Categories ── */}
      <div style={{ padding: '2.5rem 0', background: '#f8fafc' }} className="b2b-section">
        <div className="container">
          <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', marginBottom: '1.25rem' }} className="b2b-section-title">Wholesale Categories</h2>

          {loadingCats ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
              <div style={{ width: 32, height: 32, border: '3px solid #16a34a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 8px' }} />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          ) : categories.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</div>
              <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
                {catsError || 'No categories available'}
              </p>
              <p style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                {catsError ? 'Check your connection or try again shortly.' : 'Check back later for wholesale categories'}
              </p>
              {catsError && (
                <button
                  onClick={() => window.location.reload()}
                  style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                >
                  Retry
                </button>
              )}
            </div>
          ) : (
            <>
              <style>{`
                .km-b2b-grid {
                  display: grid;
                  grid-template-columns: repeat(5, 1fr);
                  gap: 12px;
                }
                @media(max-width:1024px){.km-b2b-grid{grid-template-columns:repeat(4,1fr)}}
                @media(max-width:768px){.km-b2b-grid{grid-template-columns:repeat(3,1fr)}}
                @media(max-width:480px){.km-b2b-grid{grid-template-columns:repeat(2,1fr)}}
              `}</style>
              <div className="km-b2b-grid">
                {categories.map(cat => (
                  <div
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat)}
                    style={{
                      background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px',
                      padding: '16px 12px', textAlign: 'center', cursor: 'pointer',
                      transition: 'all 0.2s', height: '130px',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = cat.color || '#16a34a'; e.currentTarget.style.boxShadow = `0 4px 12px ${cat.color || '#16a34a'}22`; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: '10px', background: cat.bg_color || '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>
                      {cat.icon || '📦'}
                    </div>
                    <p style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827', margin: 0, lineHeight: 1.3 }}>{cat.name}</p>
                    <p style={{ fontSize: '0.68rem', color: cat.color || '#16a34a', fontWeight: 600, margin: 0 }}>{cat.items_label || '10+ items'}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Popular B2B Products ── */}
      {popularProducts.length > 0 && (
        <div style={{ padding: '2.5rem 0', background: '#fff' }} className="b2b-section">
          <div className="container">
            <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', marginBottom: '1.25rem' }} className="b2b-section-title">Popular Wholesale Products</h2>
            <style>{`
              .km-b2b-pop-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:14px}
              @media(max-width:1024px){.km-b2b-pop-grid{grid-template-columns:repeat(4,1fr)}}
              @media(max-width:768px){.km-b2b-pop-grid{grid-template-columns:repeat(3,1fr)}}
              @media(max-width:480px){.km-b2b-pop-grid{grid-template-columns:repeat(2,1fr)}}
            `}</style>
            <div className="km-b2b-pop-grid">
              {popularProducts.map(product => {
                const qty     = quantities[product.id] || product.min_order || 1;
                const price   = parseFloat(product.price);
                const retail  = product.retail_price ? parseFloat(product.retail_price) : null;
                const savings = retail ? Math.round(((retail - price) / retail) * 100) : null;
                const isAdded = added[product.id];
                return (
                  <div key={product.id} style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden', position: 'relative', transition: 'box-shadow 0.2s, transform 0.2s', display: 'flex', flexDirection: 'column' }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    {isAdded && (
                      <div style={{ position: 'absolute', bottom: '8px', left: '8px', right: '8px', background: '#16a34a', color: 'white', borderRadius: '6px', padding: '5px 10px', fontSize: '0.72rem', fontWeight: 600, textAlign: 'center', zIndex: 10, boxShadow: '0 2px 8px rgba(22,163,74,0.4)' }}>
                        ✓ Added to cart
                      </div>
                    )}
                    {savings > 0 && (
                      <div style={{ position: 'absolute', top: '8px', left: '8px', background: '#dc2626', color: 'white', padding: '2px 7px', borderRadius: '20px', fontSize: '0.65rem', fontWeight: 700, zIndex: 1 }}>
                        Save {savings}%
                      </div>
                    )}
                    <div style={{ background: '#f8fafc', height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
                      {product.image_url
                        ? <img src={product.image_url} alt={product.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
                        : <span style={{ fontSize: '2.5rem' }}>📦</span>
                      }
                    </div>
                    <div style={{ padding: '10px 10px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#111827', lineHeight: 1.3, marginBottom: '2px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{product.name}{product.unit ? ` (${product.unit})` : ''}</p>
                      <p style={{ fontSize: '0.68rem', color: '#9ca3af', marginBottom: '4px' }}>Min: {product.min_order || 1}</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#16a34a' }}>₹{price}{product.unit ? `/${product.unit}` : ''}</span>
                        {retail && <span style={{ fontSize: '0.68rem', textDecoration: 'line-through', color: '#9ca3af' }}>₹{retail}</span>}
                      </div>
                      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden', flexShrink: 0 }}>
                          <button onClick={() => handleQty(product.id, qty - 1, product.min_order || 1)} disabled={qty <= (product.min_order || 1)} style={{ width: 26, height: 28, background: '#f9fafb', border: 'none', cursor: qty <= (product.min_order || 1) ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontWeight: 700, color: '#374151', opacity: qty <= (product.min_order || 1) ? 0.35 : 1 }}>−</button>
                          <span style={{ minWidth: '28px', textAlign: 'center', fontSize: '0.78rem', fontWeight: 600 }}>{qty}</span>
                          <button onClick={() => handleQty(product.id, qty + 1, product.min_order || 1)} style={{ width: 26, height: 28, background: '#f9fafb', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, color: '#374151' }}>+</button>
                        </div>
                        <button onClick={() => handleAdd(product)}
                          style={{ padding: '6px 14px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', whiteSpace: 'nowrap' }} className="b2b-add-btn-fix"
                          onMouseEnter={e => e.currentTarget.style.background = '#15803d'}
                          onMouseLeave={e => e.currentTarget.style.background = '#16a34a'}>
                          + Add
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

     
    </>
  );
}
